import React, { useEffect, useState } from 'react';
import './Home.css';
import '../App.css';
import '@fortawesome/fontawesome-free/css/all.css';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PublicationItem from '../components/PublicationItem';
import publicationsData, { publicationFilters, matchesFilter } from '../data/publications';

const primaryNewsItems = [
  { id: '2026-08-newphd', content: '8/2026: Started PhD at the University of Michigan. Go Blue!' },
  { id: '2026-06-limits', content: '6/2026: Presented my first-author paper on managing commons at LIMITS' },
  { id: '2026-04-chi', content: '4/2026: Catch me at CHI 2026 in Barcelona. My first CHI!' },
  { id: '2026-04-cse455', content: '4/2026: TA for CSE455: Computer Vision' },
  { id: '2026-03-scitalk', content: '3/2026: Attending SciTalk 2026 in Portland, OR' },
  { id: '2026-01-cse447', content: '1/2026: TA for CSE447: Natural Language Processing' },
  { id: '2025-09-cse557', content: '9/2025: TA for CSE557 graduate-level computer graphics course taught by Gilbert Bernstein' },
  { id: '2025-08-nih', content: '8/2025: I was fortunate to be featured in the NIH AI-assisted Vision for Augmented Reality report for my work at the Makeability Lab' },
  { id: '2025-08-siggraph', content: '8/2025: SIGGRAPH 2025. You can find me talking to authors after most of the paper sessions!' },
  { id: '2025-05-fox-xra', content: '5/2025: Featured on Fox 13 News Seattle for my leadership of the UW Extended Reality Association' },
  { id: '2025-03-wohesc', content: '3/2025: Speaking for the second time at The Washington Oregon Cascadia Higher Education Sustainability (WOHESC) Conference 2025' },
];

const additionalNewsItems = [
  { id: '2025-03-degree', content: "3/2025: Received my undergraduate degree from UW. I can't be here without the support of everyone who helped me along the way over the past 4 years!" },
  { id: '2024-12-king-county', content: '12/2024: Wrapped up my Internship at the King County Department of Natural Resources and Parks' },
  {
    id: '2024-12-iclr',
    content: (
      <>
        12/2024: Paper published to ICLR [<a href='https://svd-keyframe-interpolation.github.io/'>Generative Inbetweening: Adapting Image-to-Video Models for Keyframe Interpolation</a>]
      </>
    ),
  },
  { id: '2024-10-aashe', content: '10/2024: Speaking at The Association for the Advancement of Sustainability in Higher Education (AASHE) Conference 2025' },
  { id: '2024-08-siggraph', content: '8/2024: VR Theater student volunteer at SIGGRAPH 2024' },
  { id: '2024-06-awe', content: '6/2024: Student Volunteer at AWE 2024' },
  { id: '2024-03-wohesc', content: '3/2024: Speaking at The Washington Oregon Cascadia Higher Education Sustainability (WOHESC) Conference 2024' },
  {
    id: '2023-06-csf',
    content: (
      <>
        6/2023: Joined the <a href='https://csf.uw.edu/'>Campus Sustainability Fund</a> and began to study environmental issues systematically
      </>
    ),
  },
  { id: '2022-06-idtech', content: '6/2022: Taught CS at iDTech Camp' },
  {
    id: '2022-06-xra',
    content: (
      <>
        6/2022: Started to work on my first XR research project—MR-assisted Epidural Steroid Injection; revived the <a href='https://xra-uw.github.io/uw-xra-site/#/'>student XR club at UW</a>
      </>
    ),
  },
];

function NewsBlock({ className = '', showAllNews, onToggleNews }) {
  return (
    <div className={`news-block ${className}`.trim()}>
      <div className='text-line milestone'>
        <h1>News</h1>
        <ul>
          {primaryNewsItems.map((item) => (
            <li key={item.id}>{item.content}</li>
          ))}
          {showAllNews && additionalNewsItems.map((item) => (
            <li key={item.id}>{item.content}</li>
          ))}
        </ul>
        <button
          onClick={onToggleNews}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--primary-color)',
            cursor: 'pointer',
            fontSize: '1rem',
            marginTop: '10px',
            textDecoration: 'underline'
          }}
        >
          {showAllNews ? '← Show less' : 'Show more →'}
        </button>
      </div>
    </div>
  );
}

function Home() {
  const [showVisitorMap, setShowVisitorMap] = useState(false);
  const [showAllNews, setShowAllNews] = useState(false);
  const [publicationFilter, setPublicationFilter] = useState('all');

  const visiblePublications = publicationsData.filter((publication) =>
    matchesFilter(publication, publicationFilter)
  );
  // Profile video (replaced by static photo below).
  // const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  // const videoRef = React.useRef(null);

  const toggleVisitorMap = () => {
    setShowVisitorMap(!showVisitorMap);
  };

  const toggleNews = () => {
    setShowAllNews(!showAllNews);
  };

  // const handleVideoMouseEnter = () => {
  //   if (videoRef.current) {
  //     videoRef.current.currentTime = 0;
  //     videoRef.current.play();
  //   }
  // };

  // const handleVideoEnded = () => {
  //   if (videoRef.current) {
  //     videoRef.current.currentTime = 0;
  //   }
  // };

  useEffect(() => {
    // Observer for when elements enter/exit the viewport
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        } else {
          entry.target.classList.remove('animate');
        }
      });
    }, { threshold: 0.1 });
    
    const headings = document.querySelectorAll('.text-line h1');
    headings.forEach(heading => {
      observer.observe(heading);
    });

    // Variables for scroll detection
    let scrollTimeout;
    const handleScroll = () => {
      // Add animate class to visible headings during scroll
      document.querySelectorAll('.text-line h1').forEach(heading => {
        const rect = heading.getBoundingClientRect();
        const isVisible = 
          rect.top < window.innerHeight && 
          rect.bottom > 0;
        
        if (isVisible) {
          heading.classList.add('animate');
        }
      });
      
      // Clear the timeout if it's been set
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // Set a timeout to run after scrolling stops
      scrollTimeout = setTimeout(() => {
        document.querySelectorAll('.text-line h1').forEach(heading => {
          heading.classList.remove('animate');
        });
      }, 1000);
    };
    
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Cleanup function
    return () => {
      headings.forEach(heading => {
        observer.unobserve(heading);
      });
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  return (
    <>
      <Navbar />
      <div className='home-container'>

        <div className="profile-section">

          <div className="profile-left">
            <div className="profile-photo">
              <img
                src="/asset/images/CHI_2026_headshot_600.jpg"
                alt="Boe Zhou at CHI 2026, Barcelona, Spain"
                width="600"
                height="600"
                loading="eager"
                fetchpriority="high"
              />
            </div>
            <div className="contact-info contact-info--desktop">
              <div className="contact-item">
                <i className='fa-solid fa-envelope' aria-hidden="true" />
                <a href="mailto:boezhou@umich.edu">
                  Email
                  </a>
              </div>
              <div className="contact-item">
                <i className='fa-brands fa-linkedin' aria-hidden="true" />
                <a
                  href="https://www.linkedin.com/in/zby2003/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
              </div>
              <div className="contact-item">
                <i className='fa-brands fa-github' aria-hidden="true" />
                <a
                  href="https://github.com/boezzz"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub
                </a>
              </div>
              <div className="contact-item">
                <i className='fa-brands fa-x-twitter' aria-hidden="true" />
                <a
                  href="https://x.com/boe_xr"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  X
                </a>
              </div>
              <div className="contact-item">
                <i className='fa-brands fa-bluesky' aria-hidden="true" />
                <a
                  href="https://bsky.app/profile/boyzhou.bsky.social"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Bluesky
                </a>
              </div>
              <div className="contact-item">
                <i className='fa-brands fa-google-scholar' aria-hidden="true" />
                <a
                  href="https://scholar.google.com/citations?user=PPu8bK8AAAAJ"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Scholar
                </a>
              </div>
            </div>

            <NewsBlock
              className="news-block--desktop"
              showAllNews={showAllNews}
              onToggleNews={toggleNews}
            />
          </div>

          <div className="profile-main">
            <div className="profile-text">
              <h1><span className="englishname">Boyang 'Boe' Zhou</span> <span className='chinesename' lang='zh'>周博洋</span></h1>
              <div className="contact-info contact-info--mobile">
                <div className="contact-item">
                  <i className='fa-solid fa-envelope' aria-hidden="true" />
                  <a href="mailto:boezhou@umich.edu">Email</a>
                </div>
                <div className="contact-item">
                  <i className='fa-brands fa-linkedin' aria-hidden="true" />
                  <a href="https://www.linkedin.com/in/zby2003/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
                </div>
                <div className="contact-item">
                  <i className='fa-brands fa-github' aria-hidden="true" />
                  <a href="https://github.com/boezzz" target="_blank" rel="noopener noreferrer">GitHub</a>
                </div>
                <div className="contact-item">
                  <i className='fa-brands fa-x-twitter' aria-hidden="true" />
                  <a href="https://x.com/boe_xr" target="_blank" rel="noopener noreferrer">X</a>
                </div>
                <div className="contact-item">
                  <i className='fa-brands fa-bluesky' aria-hidden="true" />
                  <a href="https://bsky.app/profile/boyzhou.bsky.social" target="_blank" rel="noopener noreferrer">Bluesky</a>
                </div>
                <div className="contact-item">
                  <i className='fa-brands fa-google-scholar' aria-hidden="true" />
                  <a href="https://scholar.google.com/citations?user=PPu8bK8AAAAJ" target="_blank" rel="noopener noreferrer">Scholar</a>
                </div>
              </div>
            </div>

            <div className="bio-content">
              <p> I am a first-year PhD student at the University of Michigan, advised by <a href='https://gillespl.com/'>Lauren Gillespie</a> and <a href='https://www.cond.org/'>Eytan Adar</a>. </p>
              <div className="research-focus">
                <p>My research interest lies in Human-AI for Citizen Science, particularly in the context of ecology. Some broad questions I am working on:</p>
                <ul>
                  <li>How the way people learn ecological concepts (e.g., identifying plant species) can inspire new approaches to teaching those concepts to machine learning models, and vice versa</li>
                  <li>How participatory science and collective decision-making can be conducted through human networks, sensor networks, and agent networks</li>
                  <li>Augmenting people's perception of nature with augmented reality and contextual AI, especially for those with accessibility needs</li>
                </ul>
              </div>
              <p> I have previously earned my BS/MS in Computer Science with a minor in Environmental Studies from the University of Washington, where I was part of the <a href='https://makeabilitylab.cs.washington.edu/'>Makeability Lab</a>.</p>
              <p> My present & past mentors and collaborators include <a href='https://jonfroehlich.github.io/'>Jon Froehlich</a>, <a href='https://jaewook-lee.com/'>Jaewook Lee</a>, <a href='https://jeanne-wang.github.io/'>Xiaojuan Wang</a>, <a href='https://www.linkedin.com/in/babackelmieh'>Baback Elmieh</a> (and many more!)</p>
              {/* and  (advised by <a href='https://www.irakemelmacher.com/'>Ira Kemelmacher-Shlizerman</a>, <a href='https://www.smseitz.com/home'>Steve Seitz</a>, and <a href='https://homes.cs.washington.edu/~curless/'>Brian Curless</a>). </p> */}
              {/* <p> I work closely with <a href='https://jaewook-lee.com/'>Jaewook Lee</a>, <a href='https://www.linkedin.com/in/babackelmieh'>Baback Elmieh</a>, and <a href='https://jeanne-wang.github.io/'>Xiaojuan Wang</a> on my research.</p> */}
              {/* <p> I have previously worked at the <a href='https://realitylab.uw.edu/'>UW Reality Lab</a>, <a href='https://csf.uw.edu/'>Campus Sustainability Fund</a>, <a href='https://www.instagram.com/uw_xra/'>Extended Reality Association</a>, <a href='https://foster.uw.edu/uw-climate-risk-lab/'>Climate Risk Lab</a>, and the King County Department of Natural Resources and Parks.</p> */}
              {/* <p> Some open-source funnzies of mine: <a href='https://github.com/torchgeo/torchgeo'>TorchGeo</a>, <a href='https://github.com/dbt-labs/dbt-fusion'>dbt-Fusion</a></p> */}
            </div>

            <NewsBlock
              className="text-section news-block--mobile"
              showAllNews={showAllNews}
              onToggleNews={toggleNews}
            />
      

            <div id="research" className='text-section research-section'>
              <div className='text-line'>
                <h1>Publications</h1>
              </div>
              <div className='pub-filter'>
                <div
                  className='segmented'
                  role='group'
                  aria-label='Filter publications'
                  style={{
                    '--count': publicationFilters.length,
                    '--index': publicationFilters.findIndex((option) => option.key === publicationFilter),
                  }}
                >
                  {publicationFilters.map((option) => (
                    <button
                      key={option.key}
                      className={`segmented__btn ${publicationFilter === option.key ? 'segmented__btn--active' : ''}`}
                      aria-pressed={publicationFilter === option.key}
                      onClick={() => setPublicationFilter(option.key)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className='home-research-list'>
                {visiblePublications.map((publication) => (
                  <PublicationItem
                    key={publication.id}
                    publication={publication}
                  />
                ))}
                {publicationFilter === 'all'}
              </div>
            </div>
          </div>
        </div>

      {/* <div className='hero-container'>
      <h1>Looking for some inspirations?</h1>
      <div className='hero-btns'>
        <Button
          className='btns'
          buttonStyle='btn--outline'
          buttonSize='btn--large'
        >
        ._Check out my blogs
        </Button>
      </div>
    </div> */}
    </div>
      <Footer toggleVisitorMap={toggleVisitorMap} />
      <div style={{ textAlign: 'right' }}>
        <a href='https://mapmyvisitors.com/web/1by8m' title='Visit tracker'>
          <img src='https://mapmyvisitors.com/map.png?cl=0e1633&w=1&t=tt&d=KYawSvUri0xviH0XUuFXADAU5jT8Jz6CcEo2-YUIe20&co=0b4975&ct=cdd4d9' alt='' style={{ width: '1px', height: '1px', opacity: 0 }}/>
        </a>
      </div>

    </>
  );
}

export default Home;
